import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Form, Row, Col, Pagination } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getTransactions } from '../../api/api';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Transactions = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [transactions, setTransactions] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [summary, setSummary] = useState(null);
    const [filters, setFilters] = useState({
        type: '',
        status: '',
        startDate: null,
        endDate: null,
        page: 1,
        limit: 10,
        sortBy: 'createdAt',
        sortOrder: 'desc'
    });

    useEffect(() => {
        if (!auth.isAuthenticated || !auth.token) {
            navigate('/login', { replace: true });
            return;
        }
        
        fetchTransactions();
    }, [auth, navigate, filters]);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            
            // Prepare filter params
            const params = { ...filters };
            if (params.startDate) {
                params.startDate = params.startDate.toISOString().split('T')[0];
            }
            if (params.endDate) {
                params.endDate = params.endDate.toISOString().split('T')[0];
            }

            const response = await getTransactions(params);
            
            if (response.success) {
                setTransactions(response.data.transactions);
                setPagination(response.data.pagination);
                setSummary(response.data.summary);
            }
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
            page: 1 // Reset page when filter changes
        }));
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setFilters(prev => ({
            ...prev,
            startDate: start,
            endDate: end,
            page: 1
        }));
    };

    const handlePageChange = (page) => {
        setFilters(prev => ({
            ...prev,
            page
        }));
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'warning',
            completed: 'success',
            failed: 'danger',
            rejected: 'danger',
            cancelled: 'secondary'
        };
        return <Badge bg={variants[status] || 'secondary'}>{status.toUpperCase()}</Badge>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZone: 'UTC'  // Explicitly use UTC timezone
        });
    };

    if (loading && !transactions.length) return <Loading />;

    return (
        <>
            <PageTitle activeMenu="Transactions" motherMenu="Wallet" />

            <div className="row">
                <div className="col-12">
                    <Card>
                        <Card.Header>
                            <Card.Title>Transaction History</Card.Title>
                        </Card.Header>
                        <Card.Body>
                            {summary && (
                                <div className="row mb-4">
                                    <div className="col-xl-6 col-lg-6 col-sm-6">
                                        <div className="widget-stat card">
                                            <div className="card-body p-4">
                                                <div className="media ai-icon">
                                                    <span className="me-3 bgl-success text-success">
                                                        <i className="la la-arrow-down scale5"></i>
                                                    </span>
                                                    <div className="media-body">
                                                        <p className="mb-1">Total Deposits</p>
                                                        <h4 className="mb-0">${summary.deposits.total.toLocaleString('en-IN')}</h4>
                                                        <span className="badge badge-success">{summary.deposits.count} transactions</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-xl-6 col-lg-6 col-sm-6">
                                        <div className="widget-stat card">
                                            <div className="card-body p-4">
                                                <div className="media ai-icon">
                                                    <span className="me-3 bgl-primary text-primary">
                                                        <i className="la la-arrow-up scale5"></i>
                                                    </span>
                                                    <div className="media-body">
                                                        <p className="mb-1">Total Withdrawals</p>
                                                        <h4 className="mb-0">${summary.withdrawals.total.toLocaleString('en-IN')}</h4>
                                                        <span className="badge badge-primary">{summary.withdrawals.count} transactions</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="card-header flex-wrap border-0 pb-0 mb-4">
                                <div className="row w-100">
                                    <div className="col-md-3">
                                        <div className="form-group mb-3">
                                            <label className="form-label">Transaction Type</label>
                                            <div className="dropdown bootstrap-select default-select form-control">
                                                <Form.Select
                                                    name="type"
                                                    value={filters.type}
                                                    onChange={handleFilterChange}
                                                    className="default-select form-control"
                                                >
                                                    <option value="">All Types</option>
                                                    <option value="deposit">Deposits</option>
                                                    <option value="withdrawal">Withdrawals</option>
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="form-group mb-3">
                                            <label className="form-label">Status</label>
                                            <div className="dropdown bootstrap-select default-select form-control">
                                                <Form.Select
                                                    name="status"
                                                    value={filters.status}
                                                    onChange={handleFilterChange}
                                                    className="default-select form-control"
                                                >
                                                    <option value="">All Status</option>
                                                    <option value="pending">Pending</option>
                                                    <option value="completed">Completed</option>
                                                    <option value="failed">Failed</option>
                                                    <option value="rejected">Rejected</option>
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group mb-3">
                                            <label className="form-label">Date Range</label>
                                            <DatePicker
                                                selectsRange={true}
                                                startDate={filters.startDate}
                                                endDate={filters.endDate}
                                                onChange={handleDateChange}
                                                className="form-control"
                                                placeholderText="Select date range"
                                                maxDate={new Date()}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <div className="form-group mb-3">
                                            <label className="form-label">Sort By</label>
                                            <div className="dropdown bootstrap-select default-select form-control">
                                                <Form.Select
                                                    name="sortBy"
                                                    value={filters.sortBy}
                                                    onChange={handleFilterChange}
                                                    className="default-select form-control"
                                                >
                                                    <option value="createdAt">Date</option>
                                                    <option value="amount">Amount</option>
                                                    <option value="status">Status</option>
                                                </Form.Select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <div id="example_wrapper" className="dataTables_wrapper">
                                    <table id="example" className="display w-100 dataTable">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Date</th>
                                                <th>Reference</th>
                                                <th>Type</th>
                                                <th>Amount</th>
                                                <th>Medium</th>
                                                <th>Status</th>
                                                <th>Description</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {transactions.map((transaction, index) => (
                                                <tr key={transaction._id}>
                                                    <td>
                                                        <strong>{(pagination.page - 1) * pagination.limit + index + 1}</strong>
                                                    </td>
                                                    <td>{formatDate(transaction.createdAt)}</td>
                                                    <td>
                                                        <strong>{transaction.reference}</strong>
                                                    </td>
                                                    <td>
                                                        <span className={`badge light badge-${transaction.type === 'deposit' ? 'success' : 'primary'}`}>
                                                            {transaction.type.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <strong>{transaction.amount.toLocaleString('en-IN')}</strong>
                                                    </td>
                                                    <td>{transaction.medium}</td>
                                                    <td>
                                                        <span className={`badge light badge-${
                                                            transaction.status === 'pending' ? 'warning' : 
                                                            transaction.status === 'completed' ? 'success' : 
                                                            transaction.status === 'rejected' ? 'danger' : 'secondary'
                                                        }`}>
                                                            {transaction.status.toUpperCase()}
                                                        </span>
                                                    </td>
                                                    <td>{transaction.description}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>

                                    {pagination && (
                                        <div className="d-sm-flex text-center justify-content-between align-items-center mt-3">
                                            <div className="dataTables_info">
                                                Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                                                {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                                                {pagination.total} entries
                                            </div>
                                            <div className="dataTables_paginate paging_simple_numbers">
                                                <Pagination className="pagination-primary">
                                                    <Pagination.First
                                                        disabled={pagination.page === 1}
                                                        onClick={() => handlePageChange(1)}
                                                    />
                                                    <Pagination.Prev
                                                        disabled={pagination.page === 1}
                                                        onClick={() => handlePageChange(pagination.page - 1)}
                                                    />
                                                    {[...Array(pagination.pages)].map((_, i) => (
                                                        <Pagination.Item
                                                            key={i + 1}
                                                            active={i + 1 === pagination.page}
                                                            onClick={() => handlePageChange(i + 1)}
                                                        >
                                                            {i + 1}
                                                        </Pagination.Item>
                                                    ))}
                                                    <Pagination.Next
                                                        disabled={pagination.page === pagination.pages}
                                                        onClick={() => handlePageChange(pagination.page + 1)}
                                                    />
                                                    <Pagination.Last
                                                        disabled={pagination.page === pagination.pages}
                                                        onClick={() => handlePageChange(pagination.pages)}
                                                    />
                                                </Pagination>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            <style jsx>{`
                .widget-stat .media .media-body p {
                    font-weight: 500;
                    font-size: 14px;
                }
                .widget-stat .media .media-body h4 {
                    font-size: 24px;
                    font-weight: 600;
                }
                .scale5 {
                    font-size: 24px;
                }
                .bgl-success {
                    background: #2bc15536 !important;
                    border-radius: 5px;
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .bgl-primary {
                    background: #0d6efd36 !important;
                    border-radius: 5px;
                    width: 48px;
                    height: 48px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .badge {
                    line-height: 1.5;
                    border-radius: 4px;
                    font-size: 12px;
                    font-weight: 500;
                    padding: 5px 10px;
                }
                .light.badge-success {
                    background-color: #2bc15536;
                    color: #2bc155;
                }
                .light.badge-primary {
                    background-color: #0d6efd36;
                    color: #0d6efd;
                }
                .light.badge-warning {
                    background-color: #ffb80036;
                    color: #ffb800;
                }
                .light.badge-danger {
                    background-color: #ff4f4f36;
                    color: #ff4f4f;
                }
                .dataTables_wrapper .dataTables_paginate .pagination .page-item.active .page-link {
                    background-color: #0d6efd;
                    border-color: #0d6efd;
                    color: #fff;
                }
                .table > thead {
                    background-color: #f9f9f9;
                }
                .table > thead > tr > th {
                    font-weight: 600;
                    font-size: 14px;
                }
                .form-label {
                    font-weight: 500;
                    font-size: 14px;
                }
            `}</style>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(Transactions); 